import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Alert, Avatar, Box, Button, IconButton, Menu, MenuItem, Paper, Stack, TextField, Typography } from "@mui/material";
import ApartmentIcon from "@mui/icons-material/Apartment";
import LockIcon from "@mui/icons-material/Lock";
import TranslateIcon from "@mui/icons-material/Translate";
import { useAuth } from "../context/AuthContext";
import { useLanguage } from "../i18n/LanguageContext";
import { LANGS } from "../i18n/translations";
import { settingsApi } from "../api/resources";
import { useEffect } from "react";
import { COLORS } from "../theme";

export default function LoginPage() {
  const { login } = useAuth();
  const { t, lang, setLang } = useLanguage();
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [langAnchor, setLangAnchor] = useState(null);
  const [publicSettings, setPublicSettings] = useState(null);

  // El nombre/logo del condominio se muestran aquí aunque todavía no haya
  // sesión — es información pública de branding, no sensible.
  useEffect(() => {
    settingsApi.getPublic().then(setPublicSettings).catch(() => {});
  }, []);

  const submit = async () => {
    if (!username.trim() || !password) {
      setError(t("login.fillBoth"));
      return;
    }
    setLoading(true);
    setError("");
    try {
      await login(username.trim(), password);
      navigate("/panel");
    } catch (err) {
      setError(err.response?.data?.error || t("login.wrongCredentials"));
    } finally {
      setLoading(false);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter") submit();
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        bgcolor: COLORS.ink,
        p: 2,
        position: "relative",
      }}
    >
      <IconButton
        onClick={(e) => setLangAnchor(e.currentTarget)}
        sx={{ position: "absolute", top: 16, right: 16, color: "#C7CDD9" }}
        title={t("layout.language")}
      >
        <TranslateIcon />
      </IconButton>
      <Menu anchorEl={langAnchor} open={!!langAnchor} onClose={() => setLangAnchor(null)}>
        {LANGS.map((l) => (
          <MenuItem key={l.code} selected={l.code === lang} onClick={() => { setLang(l.code); setLangAnchor(null); }}>
            {l.label}
          </MenuItem>
        ))}
      </Menu>

      <Paper sx={{ width: "100%", maxWidth: 380, p: 4, bgcolor: COLORS.paperCard }}>
        <Stack direction="row" spacing={1.2} alignItems="center" sx={{ mb: 3 }}>
          {publicSettings?.logo ? (
            <Avatar variant="rounded" src={publicSettings.logo} sx={{ width: 34, height: 34 }} />
          ) : (
            <ApartmentIcon sx={{ color: COLORS.brass, fontSize: 30 }} />
          )}
          <Box>
            <Typography variant="h6" fontWeight={700}>
              {publicSettings?.condoName || t("login.title")}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {t("login.subtitle")}
            </Typography>
          </Box>
        </Stack>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Stack spacing={2}>
          <TextField
            label={t("login.username")}
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            onKeyDown={onKeyDown}
            autoFocus
            fullWidth
          />
          <TextField
            label={t("login.password")}
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            onKeyDown={onKeyDown}
            fullWidth
          />
          <Button variant="contained" startIcon={<LockIcon />} onClick={submit} disabled={loading} sx={{ py: 1.2 }}>
            {loading ? t("login.entering") : t("login.enter")}
          </Button>
        </Stack>
      </Paper>
    </Box>
  );
}
